import ThemedSelect from "../components/shared/ThemedSelect.jsx";
import { useEffect, useState } from "react";
import { Alert, Button, Form, Table } from "react-bootstrap";
import RulesHelp from "../components/shared/RulesHelp.jsx";
import { ClaimLink, money } from "../components/reports/reportShared.jsx";
import { findOrders, listEntityRows } from "../services/apiClient.js";

// Find Order — 1.0's OrdersSearch.aspx on the global order number
// (40_order_numbers.sql; Robert 2026-07-17). Number range / job / contractor.
// 1.0's created-date range is deferred: the legacy order Created date isn't
// imported yet (finders.js).

const EMPTY = { no_from: "", no_to: "", job: "", contractor_id: "" };

function FindOrderPage() {
  const [filters, setFilters] = useState(EMPTY);
  const [contractors, setContractors] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listEntityRows("contractor-companies").then(setContractors).catch(() => {});
  }, []);

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  async function search(e) {
    e?.preventDefault();
    setBusy(true);
    setError("");
    try {
      setResult(await findOrders(Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ""))));
    } catch (err) {
      setError(err.response?.data?.error || "Search failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="page-panel">
      <div className="page-heading"><h2>Find Order <RulesHelp topic="findOrder" /></h2></div>

      <Form onSubmit={search} className="d-flex flex-wrap align-items-end no-print">
        <Form.Group className="me-3 mb-2">
          <Form.Label className="mb-0 small fw-semibold">Order # from</Form.Label>
          <Form.Control size="sm" style={{ width: 110 }} value={filters.no_from} onChange={set("no_from")} />
        </Form.Group>
        <Form.Group className="me-3 mb-2">
          <Form.Label className="mb-0 small fw-semibold">to</Form.Label>
          <Form.Control size="sm" style={{ width: 110 }} value={filters.no_to} onChange={set("no_to")} />
        </Form.Group>
        <Form.Group className="me-3 mb-2">
          <Form.Label className="mb-0 small fw-semibold">Job #</Form.Label>
          <Form.Control size="sm" style={{ width: 110 }} value={filters.job} onChange={set("job")} />
        </Form.Group>
        <Form.Group className="me-3 mb-2">
          <Form.Label className="mb-0 small fw-semibold">Contractor</Form.Label>
          <ThemedSelect size="sm" style={{ width: 220 }} value={filters.contractor_id} onChange={set("contractor_id")}>
            <option value="">All contractors</option>
            {contractors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </ThemedSelect>
        </Form.Group>
        <Button size="sm" type="submit" className="mb-2" disabled={busy}>Search</Button>
        <Button size="sm" variant="outline-secondary" className="ms-2 mb-2"
          onClick={() => { setFilters(EMPTY); setResult(null); }}>Clear</Button>
      </Form>

      {error ? <Alert variant="danger" className="py-2">{error}</Alert> : null}

      {result ? (
        <Table striped bordered hover responsive size="sm" className="align-middle">
          <thead className="table-surface">
            <tr><th>Order #</th><th>Job #</th><th>Client</th><th>Contractor</th><th>Work Centre</th>
              <th className="text-end">Contract Given</th><th className="text-end">Invoices Received</th></tr>
          </thead>
          <tbody>
            {result.rows.map((r) => (
              <tr key={r.order_number}>
                <td>{r.order_number}</td>
                <td><ClaimLink claimId={r.claim_id} claimNumber={r.claim_number} tab="orders" /></td>
                <td>{r.client_name}</td>
                <td>{r.contractor_name || "-"}</td>
                <td>{r.work_centre_name || "-"}</td>
                <td className="text-end">{money(r.contract_given)}</td>
                <td className="text-end">{money(r.invoices_received)}</td>
              </tr>
            ))}
            {result.rows.length === 0 ? <tr><td colSpan={7} className="text-center text-muted">No orders match.</td></tr> : null}
          </tbody>
        </Table>
      ) : null}
      {result && result.rows.length >= 250 ? (
        <div className="text-muted small">Showing the newest 250 orders — narrow the search for older ones.</div>
      ) : null}
    </section>
  );
}

export default FindOrderPage;
